import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowLeftRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { DRY_SIZES, REEFER_SIZES, SIZE_LABELS, sizesFor, CONTAINER_TYPES } from "@/lib/constants";

export default function RateSearchPanel({ dark = true, initial = {} }) {
  const navigate = useNavigate();
  const [ports, setPorts] = useState([]);
  const [lines, setLines] = useState([]);
  const [form, setForm] = useState({
    shipment_type: initial.shipment_type || "export",
    pol_id: initial.pol_id || "",
    pod_id: initial.pod_id || "",
    container_type: initial.container_type || "dry",
    container_size: initial.container_size || "",
    shipping_line_id: initial.shipping_line_id || "all",
    ...initial,
  });

  useEffect(() => {
    api.get("/ports").then(({ data }) => setPorts(data));
    api.get("/shipping-lines").then(({ data }) => setLines(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const sizes = sizesFor(form.container_type);

  const submit = () => {
    const params = new URLSearchParams();
    Object.entries(form).forEach(([k, v]) => {
      if (v && v !== "all") params.set(k, v);
      if (k === "shipment_type") params.set(k, v);
    });
    navigate(`/rates?${params.toString()}`);
  };

  const labelCls = dark ? "text-white/70" : "text-muted-foreground";
  const triggerCls = dark
    ? "bg-white/10 border-white/20 text-white h-11 rounded-sm data-[placeholder]:text-white/50"
    : "bg-white border-border h-11 rounded-sm";

  return (
    <div
      className={dark ? "bg-navy/70 backdrop-blur-xl border border-white/15 rounded-md p-5 sm:p-6" : "bg-card border border-border rounded-md p-5 sm:p-6 shadow-sm"}
      data-testid="rate-search-panel"
    >
      <div className="flex gap-1 mb-5 bg-black/20 p-1 rounded-sm w-fit">
        {["export", "import"].map((t) => (
          <button
            key={t}
            onClick={() => set("shipment_type", t)}
            data-testid={`shipment-type-${t}`}
            className={`px-5 py-1.5 text-sm font-semibold rounded-sm capitalize transition-colors ${
              form.shipment_type === t
                ? "bg-signal text-white"
                : dark ? "text-white/60 hover:text-white" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <Label className={`text-xs mb-1.5 block ${labelCls}`}>POL / Origin</Label>
          <Select value={form.pol_id} onValueChange={(v) => set("pol_id", v)}>
            <SelectTrigger className={triggerCls} data-testid="pol-select"><SelectValue placeholder="Any origin" /></SelectTrigger>
            <SelectContent>
              {ports.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}, {p.country}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={`text-xs mb-1.5 block ${labelCls}`}>POD / Destination</Label>
          <Select value={form.pod_id} onValueChange={(v) => set("pod_id", v)}>
            <SelectTrigger className={triggerCls} data-testid="pod-select"><SelectValue placeholder="Any destination" /></SelectTrigger>
            <SelectContent>
              {ports.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}, {p.country}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={`text-xs mb-1.5 block ${labelCls}`}>Container Type</Label>
          <Select value={form.container_type} onValueChange={(v) => { set("container_type", v); set("container_size", ""); }}>
            <SelectTrigger className={triggerCls} data-testid="container-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONTAINER_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className={`text-xs mb-1.5 block ${labelCls}`}>Container Size</Label>
          <Select value={form.container_size} onValueChange={(v) => set("container_size", v)}>
            <SelectTrigger className={triggerCls} data-testid="container-size-select"><SelectValue placeholder="All sizes" /></SelectTrigger>
            <SelectContent>
              {sizes.map((s) => <SelectItem key={s} value={s}>{SIZE_LABELS[s]}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="lg:col-span-2">
          <Label className={`text-xs mb-1.5 block ${labelCls}`}>Shipping Line</Label>
          <Select value={form.shipping_line_id} onValueChange={(v) => set("shipping_line_id", v)}>
            <SelectTrigger className={triggerCls} data-testid="line-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Shipping Lines</SelectItem>
              {lines.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="lg:col-span-2 flex items-end">
          <Button
            onClick={submit}
            className="w-full h-11 bg-signal hover:bg-signal/90 text-white font-semibold rounded-sm"
            data-testid="rate-search-submit"
          >
            <Search className="h-4 w-4 mr-2" /> Search Freight Rates
          </Button>
        </div>
      </div>
    </div>
  );
}
